// Bottom product tab bar — phones only (AppShell mounts it under <main> when
// usePhone() is true, portrait and landscape). Five 56 px items: Overview,
// Trade, Asset, Health and More; the active product is drawn in its own
// colour (the lime budget allows the active-nav marker when it is Trade).
// "More" opens a bottom sheet with Compliance, Data, Organisation and Account
// settings — every product stays one or two taps away without the hamburger,
// which keeps page-level navigation. Safe-area padding sits below the row.
// The Asset item carries the unread-alert badge from the WS channel.

import { useMemo, useState } from "react";
import { preloadRoute } from "@/app/routePreload";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, MoreHorizontal, UserCog } from "lucide-react";

import { useAuth } from "@/lib/auth/AuthProvider";
import { useWsClient } from "@/lib/ws/WsProvider";
import { appForPath, visibleApps, type AppId, type ConsoleApp } from "@/app/products";
import { Drawer, ProductIcon } from "@/app/console/ui";

const PRIMARY: ReadonlyArray<AppId> = ["overview", "trade", "asset", "health"];

const ITEM =
  "relative flex h-full w-full flex-col items-center justify-center gap-1 text-[11px] font-medium leading-3 transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand/60";

export function BottomTabBar() {
  const { roles } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { unreadAlerts } = useWsClient();
  const [moreOpen, setMoreOpen] = useState(false);

  const apps = useMemo(() => visibleApps(roles), [roles]);
  const primary = useMemo(
    () => PRIMARY.map((id) => apps.find((a) => a.id === id)).filter((a): a is ConsoleApp => Boolean(a)),
    [apps],
  );
  const more = useMemo(() => apps.filter((a) => !PRIMARY.includes(a.id)), [apps]);
  const active = appForPath(location.pathname);
  const moreActive = more.some((a) => a.id === active.id);

  function go(to: string) {
    setMoreOpen(false);
    preloadRoute(to);
    navigate(to);
  }

  return (
    <>
      <nav
        aria-label="Products"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface-1/95 backdrop-blur supports-[backdrop-filter]:bg-surface-1/85"
        style={{ paddingBottom: "var(--pb-safe-bottom)", paddingLeft: "var(--pb-safe-left)", paddingRight: "var(--pb-safe-right)" }}
      >
        <ul className="grid h-[var(--pb-tabbar-h)] grid-cols-5">
          {primary.map((app) => {
            const isActive = app.id === active.id;
            const Icon = app.icon;
            const badge = app.id === "asset" ? unreadAlerts : 0;
            return (
              <li key={app.id} className="min-w-0">
                <Link
                  to={app.home}
                  aria-current={isActive ? "page" : undefined}
                  aria-label={badge > 0 ? `${app.name} — ${badge} unread alert${badge === 1 ? "" : "s"}` : undefined}
                  className={`${ITEM} ${isActive ? "" : "text-text-3 hover:text-text-1"}`}
                  style={isActive ? { color: app.accent } : undefined}
                >
                  {isActive && <span className="absolute inset-x-4 top-0 h-0.5 rounded-b" style={{ backgroundColor: app.accent }} aria-hidden="true" />}
                  <span className="relative">
                    <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
                    {badge > 0 && (
                      <span className="absolute -right-2 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-danger px-1 font-mono text-[9px] font-semibold text-black" aria-hidden="true">
                        {badge > 99 ? "99+" : badge}
                      </span>
                    )}
                  </span>
                  <span className="max-w-full truncate">{app.name}</span>
                </Link>
              </li>
            );
          })}
          <li className="min-w-0">
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={moreOpen}
              aria-current={moreActive ? "page" : undefined}
              className={`${ITEM} ${moreActive ? "" : "text-text-3 hover:text-text-1"}`}
              style={moreActive ? { color: active.accent } : undefined}
            >
              {moreActive && <span className="absolute inset-x-4 top-0 h-0.5 rounded-b" style={{ backgroundColor: active.accent }} aria-hidden="true" />}
              <MoreHorizontal className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
              <span>More</span>
            </button>
          </li>
        </ul>
      </nav>

      <Drawer open={moreOpen} onClose={() => setMoreOpen(false)} title="More" description="Compliance, Data, Organisation and your account." width="sm">
        <ul className="-mx-1 space-y-0.5" aria-label="More products">
          {more.map((app) => (
            <li key={app.id}>
              <MoreRow
                icon={<ProductIcon icon={app.icon} accent={app.accent} size="sm" />}
                label={app.name}
                hint={app.tagline}
                active={app.id === active.id && !location.pathname.startsWith("/settings")}
                onClick={() => go(app.home)}
              />
            </li>
          ))}
          <li>
            <MoreRow
              icon={
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-line bg-surface-2 text-text-2" aria-hidden="true">
                  <UserCog className="h-[18px] w-[18px]" strokeWidth={1.75} />
                </span>
              }
              label="Account settings"
              hint="Profile, MFA & preferences"
              active={location.pathname.startsWith("/settings")}
              onClick={() => go("/settings")}
            />
          </li>
        </ul>
      </Drawer>
    </>
  );
}

function MoreRow({ icon, label, hint, active, onClick }: { icon: React.ReactNode; label: string; hint: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`flex min-h-14 w-full items-center gap-3 rounded-control px-2 text-left transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 ${
        active ? "bg-white/[0.06]" : "hover:bg-white/[0.04]"
      }`}
    >
      {icon}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-text-1">{label}</span>
        <span className="block truncate text-xs leading-4 text-text-3">{hint}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-text-3" aria-hidden="true" />
    </button>
  );
}
